// src/components/home/CustomCursor.jsx
import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";

const CORAL = "#FF6A4D";
// Same 4-point "generative spark" path as the brand logo (src/components/
// Logo.jsx), so the cursor trail reads as an extension of the brand mark,
// not an unrelated shape.
const STAR_PATH = "M20 9 C21 16 24 19 31 20 C24 21 21 24 20 31 C19 24 16 21 9 20 C16 19 19 16 20 9 Z";

// Flat arrow silhouette (classic pointer shape), same flat coral fill as the
// sparkle — gives the pointer a persistent glyph, not just the fading trail.
const ARROW_PATH = "M4 2 L4 20 L8.5 16.5 L11.5 22.5 L14 21 L11 15 L17 15 Z";
const ARROW_SIZE = 22;

const SPAWN_INTERVAL_MS = 45;
const LIFETIME_MS = 650;

// Desktop/fine-pointer only — Home.jsx mounts this behind useCapability()'s
// renderCursor gate. Real cursor is hidden via CSS on <body> only while this
// is mounted, so a failed/unmounted state never leaves the user without any
// cursor at all.
//
// Plain DOM/CSS, no canvas/WebGL: a small coral sparkle-star (the logo's own
// "generative spark" shape) spawns at the pointer's position every ~45ms of
// movement, scales up then fades out over ~650ms, then removes itself —
// imperative `createElement`/`appendChild`/`remove()` rather than React
// state, since this runs on every pointermove and a per-event re-render
// would be wasteful (same discipline this codebase uses for any
// per-frame/high-frequency visual update).
export default function CustomCursor() {
  const containerRef = useRef(null);
  const lastSpawnRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const arrow = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    arrow.setAttribute("viewBox", "0 0 24 24");
    arrow.setAttribute("width", String(ARROW_SIZE));
    arrow.setAttribute("height", String(ARROW_SIZE));
    arrow.style.position = "fixed";
    arrow.style.left = "0px";
    arrow.style.top = "0px";
    arrow.style.pointerEvents = "none";
    arrow.style.zIndex = "2001";
    arrow.style.display = "none";
    const arrowPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
    arrowPath.setAttribute("d", ARROW_PATH);
    arrowPath.setAttribute("fill", CORAL);
    arrow.appendChild(arrowPath);
    container.appendChild(arrow);

    const onMove = (e) => {
      arrow.style.display = "block";
      arrow.style.transform = `translate(${e.clientX - 4}px, ${e.clientY - 2}px)`;

      const now = performance.now();
      if (now - lastSpawnRef.current < SPAWN_INTERVAL_MS) return;
      lastSpawnRef.current = now;

      const size = 12 + Math.random() * 6;
      const rotation = Math.random() * 360;
      const drift = (Math.random() - 0.5) * 16;

      const star = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      star.setAttribute("viewBox", "0 0 40 40");
      star.setAttribute("width", String(size));
      star.setAttribute("height", String(size));
      star.style.position = "fixed";
      star.style.left = `${e.clientX}px`;
      star.style.top = `${e.clientY}px`;
      star.style.transform = `translate(-50%, -50%) rotate(${rotation}deg)`;
      star.style.pointerEvents = "none";
      star.style.zIndex = "2000";
      star.style.opacity = "0";
      star.style.transition = `opacity ${LIFETIME_MS * 0.3}ms ease-out, transform ${LIFETIME_MS}ms cubic-bezier(0.22,1,0.36,1)`;

      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", STAR_PATH);
      path.setAttribute("fill", CORAL);
      star.appendChild(path);
      container.appendChild(star);

      // Force a style flush so the transition below actually animates from
      // the initial state instead of jumping straight to the end state.
      // eslint-disable-next-line no-unused-expressions
      star.getBoundingClientRect();
      star.style.opacity = "0.9";
      star.style.transform = `translate(calc(-50% + ${drift}px), calc(-50% + ${drift}px)) rotate(${rotation + 25}deg) scale(1.3)`;

      window.setTimeout(() => {
        star.style.opacity = "0";
      }, LIFETIME_MS * 0.55);
      window.setTimeout(() => {
        star.remove();
      }, LIFETIME_MS);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      arrow.remove();
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      aria-hidden="true"
      sx={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 2000, overflow: "hidden" }}
    />
  );
}
